import { Injectable, computed, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of, Subject, throwError } from 'rxjs';
import { tap, catchError, finalize, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { TokenStorageService } from './token-storage.service';
import { LoggerService } from './logger.service';
import { ActingSession, PartnerService } from './partner.service';

export interface RegisterData {
  first_name: string;
  last_name: string;
  name: string;
  street: string;
  city: string;
  zip: string;
  country: string;
  phone: string;
  website?: string;
  email: string;
  password: string;
  suburl: string;
}

export interface InitialRegisterData {
  email: string;
  password: string;
  tracking_data?: Record<string, string>;
  /** From a network's invite link: registers the account as that network's member. */
  verbund_invite_token?: string | null;
  /** From an affiliate's register link (?ref=CODE); unknown codes are ignored. */
  affiliate_code?: string | null;
}

export interface MemberProfileData {
  name: string;
  website?: string;
}

// Step-by-step profile data interfaces
export interface ProfileStep1Data {
  first_name: string;
  last_name: string;
  phone: string;
}

export interface ProfileStep2Data {
  company_name: string;
  billing_street: string;
  billing_city: string;
  billing_zip: string;
  billing_country: string;
}

export interface ProfileStep3Data {
  use_billing_as_business: boolean;
  business_street?: string;
  business_city?: string;
  business_zip?: string;
  business_country?: string;
}

export interface ProfileStep4Data {
  name: string; // Shop/brand name
  website?: string;
  suburl: string;
}

export interface ProfileCompleteData {
  first_name: string;
  last_name: string;
  name: string; // Shop/brand name (for vouchers and storefront)
  company_name: string; // Official company name (for invoices, required)

  // Billing address
  billing_street: string;
  billing_city: string;
  billing_zip: string;
  billing_country: string;

  // Business address
  use_billing_as_business: boolean;
  business_street?: string;
  business_city?: string;
  business_zip?: string;
  business_country?: string;

  phone: string;
  website?: string;
  suburl: string;
}

export interface LoginData {
  username: string;
  password: string;
}

/** Taking over an account an agency created (from the claim mail). */
export interface AccountClaimInfo {
  email: string;
  company_name: string | null;
  partner_name: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  public_company_id: string;
  company_name: string;
  company_is_stripe_onboarding_success: boolean;
  company_stripe_account_id: string;
  company_is_profile_completed: boolean;
  is_new_account?: boolean; // true when a Google login just created the account
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private router: Router,
    private tokenStorage: TokenStorageService,
    private logger: LoggerService,
    private partnerService: PartnerService,
  ) {}

  private loginSuccess = new Subject<string>();
  public loginSuccess$ = this.loginSuccess.asObservable();

  /**
   * Reactive auth state.
   *
   * `isLoggedIn()` reads localStorage and is therefore invisible to signals —
   * nothing can react to a login or logout. Resources need exactly that, so the
   * two transitions (persistAuthResponse / clearAll) bump this instead.
   */
  private readonly authTick = signal(0);
  private isLoggingOut = false;
  readonly isAuthenticated = computed(() => {
    this.authTick();
    return this.isLoggedIn();
  });
  readonly publicCompanyId = computed(() => {
    this.authTick();
    return this.tokenStorage.getPublicCompanyId();
  });
  /**
   * The agency working in this account, or null when the customer is logged in. An
   * agency can do everything except change the account's own login and its access.
   */
  readonly companyName = computed(() => {
    this.authTick();
    return this.tokenStorage.getCompanyName();
  });
  readonly actingPartnerName = computed(() => {
    this.authTick();
    return this.tokenStorage.getActingPartnerName();
  });

  register(data: RegisterData): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/register`, data);
  }

  initialRegister(data: InitialRegisterData): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/initial-register`, data);
  }

  completeProfile(data: ProfileCompleteData): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/complete-profile`, data);
  }

  // Step-by-step profile completion methods
  completeProfileStep1(data: ProfileStep1Data): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/profile/step1`, data);
  }

  completeProfileStep2(data: ProfileStep2Data): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/profile/step2`, data);
  }

  completeProfileStep3(data: ProfileStep3Data): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/profile/step3`, data);
  }

  completeProfileStep4(data: ProfileStep4Data): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/profile/step4`, data);
  }

  /** A network member's last onboarding step: its name and payout IBAN (no shop, no Stripe). */
  completeMemberProfile(data: MemberProfileData): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/profile/member`, data);
  }

  // Get current onboarding profile data
  getOnboardingProfile(): Observable<any> {
    return this.http.get(`${this.apiUrl}/auth/profile/onboarding`);
  }

  // Get current company data using existing companies endpoint
  getCurrentCompany(): Observable<any> {
    const publicCompanyId = this.tokenStorage.getPublicCompanyId();
    if (!publicCompanyId) {
      throw new Error('No company ID found');
    }
    return this.http.get(`${this.apiUrl}/companies/${publicCompanyId}`);
  }

  // Refresh company data in token storage after profile completion
  refreshCompanyData(): Observable<any> {
    return this.getCurrentCompany().pipe(
      tap((companyData) => {
        this.logger.log('🔄 [AuthService] Refreshing company data in token storage', companyData);

        // Update token storage with complete fresh data from backend
        this.tokenStorage.setCompanyData({
          publicCompanyId: companyData.public_id,
          companyName: companyData.name,
          companyStripeAccountId: companyData.stripe_account_id,
          companyIsStripeOnboardingSuccess: companyData.is_stripe_onboarding_success,
          companyIsProfileCompleted: companyData.is_profile_completed,
        });

        this.logger.log('✅ [AuthService] Company data refreshed successfully');
      }),
    );
  }

  login(data: LoginData): Observable<AuthResponse> {
    const formData = new URLSearchParams();
    formData.set('grant_type', '');
    formData.set('username', data.username);
    formData.set('password', data.password);
    formData.set('scope', '');
    formData.set('client_id', '');
    formData.set('client_secret', '');

    return this.http
      .post<AuthResponse>(`${this.apiUrl}/auth/login`, formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          accept: 'application/json',
        },
        withCredentials: true,
      })
      .pipe(tap((response) => this.persistAuthResponse(response)));
  }

  // mode defaults to the non-destructive 'login' (never creates an account);
  // only the register flow passes 'register'
  loginWithGoogle(
    credential: string,
    trackingData?: Record<string, string>,
    mode: 'login' | 'register' = 'login',
    verbundInviteToken?: string | null,
    affiliateCode?: string | null,
  ): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(
        `${this.apiUrl}/auth/google`,
        {
          credential,
          tracking_data: trackingData,
          mode,
          verbund_invite_token: verbundInviteToken ?? undefined,
          affiliate_code: affiliateCode ?? undefined,
        },
        { withCredentials: true },
      )
      .pipe(tap((response) => this.persistAuthResponse(response)));
  }

  linkGoogle(credential: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/google/link`, { credential });
  }

  unlinkGoogle(): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/google/unlink`, {});
  }

  private persistAuthResponse(response: AuthResponse): void {
    this.tokenStorage.setAccessToken(response.access_token);
    this.tokenStorage.setCompanyData({
      publicCompanyId: response.public_company_id,
      companyName: response.company_name,
      companyStripeAccountId: response.company_stripe_account_id,
      companyIsStripeOnboardingSuccess: response.company_is_stripe_onboarding_success,
      companyIsProfileCompleted: response.company_is_profile_completed,
    });
    this.authTick.update((n) => n + 1);
    this.loginSuccess.next(response.public_company_id);
  }

  /** An agency switches into a client's account (company session naming the agency). */
  startActingSession(session: ActingSession): void {
    this.tokenStorage.clearAll();
    this.tokenStorage.setActingPartnerName(session.acting_partner_name);
    this.persistAuthResponse({
      ...session,
      company_stripe_account_id: session.company_stripe_account_id ?? '',
    });
  }

  claimInfo(token: string): Observable<AccountClaimInfo> {
    return this.http.get<AccountClaimInfo>(`${this.apiUrl}/auth/claim`, { params: { token } });
  }

  /** The customer takes over the account the agency created and is logged in. */
  claimAccount(token: string, password: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(
        `${this.apiUrl}/auth/claim`,
        { token, password, accept_terms: true },
        { withCredentials: true },
      )
      .pipe(
        tap((response) => {
          // Whoever was logged in before (e.g. the agency) is replaced
          this.tokenStorage.clearAll();
          this.persistAuthResponse(response);
        }),
      );
  }

  refreshToken(): Observable<AuthResponse> {
    if (this.tokenStorage.getActingPartnerName()) {
      return this.renewActingSession();
    }
    return this.http
      .post<AuthResponse>(
        `${this.apiUrl}/auth/refresh`,
        {},
        {
          withCredentials: true, // Important: send refresh token from HttpOnly cookie
        },
      )
      .pipe(
        tap((response) => {
          this.tokenStorage.setAccessToken(response.access_token);
        }),
        catchError((error) => {
          if (error.status === 401) {
            this.logout();
          }
          throw error;
        }),
      );
  }

  /**
   * The agency's company token has no refresh cookie: a new one comes from the
   * partner session (which renews itself through partnerAuthInterceptor).
   */
  private renewActingSession(): Observable<AuthResponse> {
    const publicCompanyId = this.tokenStorage.getPublicCompanyId();
    if (!publicCompanyId) {
      return throwError(() => new HttpErrorResponse({ status: 401 }));
    }
    return this.partnerService.openClient(publicCompanyId).pipe(
      tap((session) => {
        this.tokenStorage.setAccessToken(session.access_token);
        this.tokenStorage.setActingPartnerName(session.acting_partner_name);
      }),
      map((session) => ({
        ...session,
        company_stripe_account_id: session.company_stripe_account_id ?? '',
      })),
      catchError((error) => {
        // 404: the customer ended the access. The partner area shows what's left.
        if (error instanceof HttpErrorResponse && (error.status === 404 || error.status === 401)) {
          this.leaveActingSession();
        }
        return throwError(() => error);
      }),
    );
  }

  /** The agency leaves the client's account and returns to its partner area. */
  leaveActingSession(): void {
    this.clearSession();
    this.router.navigate(['/partner/clients']);
  }

  logout(): void {
    // An agency only leaves the client's account; its partner session stays. Never
    // call /auth/logout then: it would end the browser's own company session, if any.
    if (this.tokenStorage.getActingPartnerName()) {
      this.leaveActingSession();
      return;
    }
    // Several failing requests can trigger a logout at once; one is enough
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    // Add a request to backend to invalidate the refresh token. The local session is
    // cleared whatever the outcome: with an expired session this request fails too,
    // and the user would otherwise stay stuck on a page that can never load.
    this.http
      .post(
        `${this.apiUrl}/auth/logout`,
        {},
        {
          withCredentials: true,
        },
      )
      .pipe(
        finalize(() => {
          this.clearSession();
          this.isLoggingOut = false;
          this.router.navigate(['/login']);
        }),
      )
      .subscribe({
        error: (error) => this.logger.warn('⚠️ [AuthService] Logout request failed:', error),
      });
  }

  /**
   * Drops the local session without asking the backend. For a session the backend
   * already rejected: leaving the stale token behind made `isLoggedIn()` keep
   * reporting true, so the login page redirected straight back into the guard.
   */
  clearSession(): void {
    this.tokenStorage.clearAll();
    this.authTick.update((n) => n + 1);
  }

  verifyAuth(): Observable<boolean> {
    // Keep the short-circuit: without a token there is nothing to verify and the
    // interceptor would send an anonymous request. The header itself comes from
    // the interceptor.
    if (!this.tokenStorage.getAccessToken()) return of(false);

    return this.http.get<boolean>(`${this.apiUrl}/auth/verify`).pipe(
      catchError((error) => {
        if (error.status === 401) {
          return of(false);
        }
        throw error;
      }),
    );
  }

  /**
   * Simple synchronous check if user is logged in (without API call)
   * Returns true if access token exists, false otherwise
   */
  isLoggedIn(): boolean {
    const token = this.tokenStorage.getAccessToken();
    const companyId = this.tokenStorage.getPublicCompanyId();

    // User is considered logged in if both token and company ID exist
    return !!(token && companyId);
  }

  /**
   * Check if user has completed their profile
   */
  isProfileCompleted(): boolean {
    return this.tokenStorage.getCompanyIsProfileCompleted();
  }

  changePassword(oldPassword: string, newPassword: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/change-password`, {
      old_password: oldPassword,
      new_password: newPassword,
    });
  }

  changeEmail(newEmail: string, currentPassword: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/change-email`, {
      new_email: newEmail,
      current_password: currentPassword,
    });
  }

  requestPasswordReset(email: string) {
    // The backend builds the link in the e-mail itself, never from the request
    return this.http.post(`${this.apiUrl}/auth/request-password-reset`, { email });
  }

  resetPassword(token: string, newPassword: string) {
    return this.http.post(`${this.apiUrl}/auth/reset-password`, {
      token: token,
      new_password: newPassword,
    });
  }

  verifyEmail(token: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/verify-email`, { token });
  }

  confirmEmailChange(token: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/auth/verify-new-email`, { params: { token } });
  }

  resendVerificationEmail(email: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/resend-verification-email`, { email });
  }
}
